import {
  CombinedClassification,
  CombinedObservation,
  TellescopeModelFromName,
  useAuth,
  usePermissionQuery,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQueries,
  useVitalsQueryWithFilters,
} from "@tellescope/tanstack-query";
import { Badge, Button, cn, VirtualizedColumnDef, VirtualizedDataTableProps } from "@tellescope/ui";
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { useTableView } from "./useTableView";
import { useTableViewConfig, useTableViewLocalOverrides } from "./useTableViewConfig";
import { DEFAULT_VITALS_VIEW, useShallowVitalsDefaultViewStore } from "../store";
import { i18n } from "@tellescope/i18n";
import { EnduserFieldCell } from "../components/tables";
import { formatName, getMedStatusTranslations, parseVitalProgramConfigurationValue } from "@tellescope/utils";
import { DateCell } from "../components/tickets/cells";
import {
  ClassificationCell,
  FlagsCell,
  LookbackVisibilityCell,
  ReviewButtonCell,
  ValuesCell,
  VitalsActionsCell,
} from "../components/vitals";
import { useTranslation } from "react-i18next";
import { useShallowSidebarStore } from "../store/sidebar-store";
import { TELLESCOPE_2_TABLE_VIEW_PREFIX } from "./useTableView";
import { getVitalsClassificationTranslations } from "../constants";

const CLASSIFICATION_PRIORITIES: Record<string, number> = {
  Target: 0,
  High: 10,
  "Very High": 100,
  "Critical High": 1000,
  Low: 10,
  "Very Low": 100,
  "Critical Low": 1000,
} as const;

const getHighestClassification = (classifications: CombinedClassification[]) =>
  classifications.reduce<{ priority: number; label: string }>(
    (acc, curr) => {
      const newPriority = CLASSIFICATION_PRIORITIES[curr.classification];
      if (newPriority !== undefined && newPriority > acc.priority) {
        return {
          priority: newPriority,
          label: curr.classification,
        };
      }
      return acc;
    },
    { priority: -1, label: "" }
  );

/**
 * Resolves a single classification for an observation based on selected threshold config IDs.
 * - If no thresholds selected: returns undefined (no coloring)
 * - If exactly 1 classification: returns it unconditionally
 * - If multiple: returns first matching a selected threshold
 */
export const resolveClassificationForThresholds = (
  classifications: CombinedClassification[],
  selectedThresholdIds: string[] | undefined
): CombinedClassification | undefined => {
  if (!selectedThresholdIds?.length) return classifications[0];
  if (classifications.length === 1) return classifications[0];
  return classifications.find((c) => selectedThresholdIds.includes(c.configurationId));
};

/**
 * Maps an observation's measurements to include their resolved classification
 * based on the selected threshold config IDs.
 */
export const getMeasurementsWithClassifications = (
  observation: CombinedObservation,
  selectedThresholdIds: string[] | undefined
) =>
  observation.measurements.map((measurement) => {
    const observationClassifications = observation.classifications.filter(
      (c) => c.observationId === measurement.observationId
    );
    const classification = resolveClassificationForThresholds(observationClassifications, selectedThresholdIds);
    return { measurement, classification };
  });

/** Filters a list of classifications to only those matching selected thresholds. */
const filterClassificationsByThresholds = (
  classifications: CombinedClassification[],
  selectedThresholdIds: string[] | undefined
): CombinedClassification[] => {
  if (!selectedThresholdIds?.length) return [];
  if (classifications.length === 1) return classifications;
  return classifications.filter((c) => selectedThresholdIds.includes(c.configurationId));
};

type VitalsColumnDef = VirtualizedColumnDef<CombinedObservation>;
type VitalsColumnFieldName = (typeof DEFAULT_VITALS_VIEW)["columns"][number]["field"];

// Partial column config without width (width comes from view configuration)
type PartialVitalsColumnDef<Value = unknown> = Partial<VitalsColumnDef> & {
  label?: string;
  cell?: VitalsColumnDef["cell"];
} & (
    | {
        accessorKey?: keyof CombinedObservation;
      }
    | {
        accessorFn: (originalRow: CombinedObservation, index: number) => Value;
      }
  );

export type VitalsReviewedFilter = "all" | "reviewed" | "unreviewed";

export type VitalsValidStatusFilter = "all" | "valid" | "invalid";

export interface VitalsDateRangeFilter {
  from?: number;
  to?: number;
}

export interface VitalsFilters {
  reviewed?: VitalsReviewedFilter;
  classification?: {
    type: "any" | "all";
    triggers: string[];
  };
  source?: string[];
  enduserIds?: string[];
  careTeamIds?: string[];
  dateRange?: VitalsDateRangeFilter;
  programId?: string;
  threshholds?: string[];
  validStatus?: VitalsValidStatusFilter;
}

export const vitalsFiltersAreEqual = (a: VitalsFilters, b: VitalsFilters): boolean => {
  return JSON.stringify(a) === JSON.stringify(b);
};

export const countVitalsActiveFilters = (filters: VitalsFilters): number => {
  let count = 0;
  if (filters.reviewed && filters.reviewed !== "all") count++;
  if (filters.classification && filters.classification.triggers.length > 0) count++;
  if (filters.source && filters.source.length > 0) count++;
  if (filters.enduserIds && filters.enduserIds.length > 0) count++;
  if (filters.careTeamIds && filters.careTeamIds.length > 0) count++;
  if (filters.dateRange?.from !== undefined || filters.dateRange?.to !== undefined) count++;
  if (filters.threshholds?.length) count++;
  if (filters.validStatus && filters.validStatus !== "valid") count++;
  return count;
};

type VitalProgramsData = {
  programs: { title: string; configurationIds: string[] }[];
  firstProgramConfigIds: string[] | undefined;
};

export interface UseVitalsTableArgs {
  viewId?: string;
  setViewId?: Dispatch<SetStateAction<string>>;
}

export const useVitalsTable = ({ viewId: providedViewId, setViewId }: UseVitalsTableArgs = {}) => {
  const { t } = useTranslation("vitals");
  const { currentUser } = useAuth();
  const userId = currentUser.id;
  const { openSidebar } = useShallowSidebarStore("openSidebar");
  const { update: updatePermission } = usePermissionQuery({
    model: "enduser_observations",
    actions: ["update"],
  });
  const canInvalidate = updatePermission.access;

  const { defaultViewByUserId, setUserDefaultView } = useShallowVitalsDefaultViewStore(
    "defaultViewByUserId",
    "setUserDefaultView"
  );

  const localOverrides = useTableViewLocalOverrides<VitalsFilters>(providedViewId ?? "default-view");

  // Fetch vital programs to resolve default threshold configuration IDs
  const { data: vitalPrograms } = useTellescopeInfiniteQuery({
    model: "configurations",
    method: "getSome",
    request: {
      limit: 500,
      filter: {
        type: "Vital Programs",
      },
    },
    select: (data): VitalProgramsData => {
      return data.pages.flat().reduce<VitalProgramsData>(
        (acc, program) => {
          const { programs } = parseVitalProgramConfigurationValue(program.value);
          acc.programs.push(...programs);
          if (!acc.firstProgramConfigIds && programs[0]?.configurationIds.length) {
            acc.firstProgramConfigIds = programs[0].configurationIds;
          }
          return acc;
        },
        { programs: [], firstProgramConfigIds: undefined }
      );
    },
  });

  const fieldToColumnConfigMap: Record<VitalsColumnFieldName, PartialVitalsColumnDef> = {
    Enduser: {
      accessorFn: ({ enduserId }) => {
        const enduser = endusersQueries[enduserId];
        if (!enduser) {
          return enduserId;
        }
        return formatName(enduser);
      },
      label: i18n.t("Name", { ns: "vitals" }),
      width: "2fr",
      cell: ({ row }) => (
        <Button
          variant="link"
          onClick={(e) => {
            e.stopPropagation();
            openSidebar("ContactDetails", { enduserId: row.original.enduserId, selectedSection: "Vitals" });
          }}
          className={cn("truncate w-full justify-start", row.original.status === "entered-in-error" && "line-through")}
        >
          <EnduserFieldCell
            enduserId={row.original.enduserId}
            fieldSelector={(enduser) => formatName(enduser) || t("Patient: {{id}}", { id: row.original.enduserId })}
          />
        </Button>
      ),
    },
    Timestamp: {
      accessorKey: "timestamp",
      label: i18n.t("Timestamp", { ns: "vitals" }),
      width: "2fr",
      cell: ({ row }) => (
        <span className={row.original.status === "entered-in-error" ? "line-through" : undefined}>
          <DateCell timestamp={row.original.timestamp} />
        </span>
      ),
    },
    Flags: {
      accessorFn: ({ medStatus, beforeMeal, irregularHeartbeat }) => {
        const shownFlags = [
          medStatus ? getMedStatusTranslations()[medStatus] : undefined,
          beforeMeal === undefined ? undefined : beforeMeal ? t("Before Meal") : t("After Meal"),
          irregularHeartbeat === undefined ? undefined : irregularHeartbeat ? t("Irregular") : t("Regular"),
        ].filter((val) => val !== undefined && val !== "");
        return `${shownFlags.length}-${shownFlags.join("-")}`;
      },
      label: i18n.t("Flags", { ns: "vitals" }),
      width: "2fr",
      cell: ({ row }) => (
        <span className={row.original.status === "entered-in-error" ? "line-through" : undefined}>
          <FlagsCell
            medStatus={row.original.medStatus}
            beforeMeal={row.original.beforeMeal}
            irregularHeartbeat={row.original.irregularHeartbeat}
            observationIds={row.original.measurements.map(({ observationId }) => observationId)}
          />
        </span>
      ),
    },
    Values: {
      accessorFn: ({ classifications }) => {
        const filtered = filterClassificationsByThresholds(classifications, effectiveThresholdIds);
        return getHighestClassification(filtered).priority;
      },
      label: i18n.t("Values", { ns: "vitals" }),
      width: "6fr",
      cell: ({ row }) => {
        return (
          <ValuesCell
            measurementsWithClassifications={getMeasurementsWithClassifications(row.original, effectiveThresholdIds)}
            className={row.original.status === "entered-in-error" ? "line-through" : undefined}
          />
        );
      },
    },
    Classification: {
      accessorFn: ({ classifications }) => {
        const filtered = filterClassificationsByThresholds(classifications, effectiveThresholdIds);
        return getHighestClassification(filtered).priority;
      },
      label: i18n.t("Classification", { ns: "vitals" }),
      width: "2fr",
      cell: ({ row }) => {
        const filtered = filterClassificationsByThresholds(row.original.classifications, effectiveThresholdIds);
        const { label } = getHighestClassification(filtered);
        return (
          <ClassificationCell
            classification={label}
            className={row.original.status === "entered-in-error" ? "line-through" : undefined}
          >
            {getVitalsClassificationTranslations()[label]}
          </ClassificationCell>
        );
      },
    },
    Source: {
      accessorKey: "source",
      label: i18n.t("Source", { ns: "vitals" }),
      width: "3fr",
      cell: ({ row }) => {
        if (!row.original.source) return null;
        return (
          <Badge
            variant="muted"
            className={cn("font-normal", row.original.status === "entered-in-error" && "line-through")}
          >
            {row.original.source}
          </Badge>
        );
      },
    },
    Reviewed: {
      accessorKey: "reviewedAt",
      label: i18n.t("Reviewed", { ns: "vitals" }),
      width: "2fr",
      cell: ({ row }) => (
        <ReviewButtonCell
          observationIds={row.original.measurements.map(({ observationId }) => observationId)}
          isReviewed={!!row.original.reviewedAt}
          count={row.original.measurements.length}
        />
      ),
    },
    Visible: {
      accessorKey: "excludeFromVitalCountLookback",
      label: i18n.t("Visible", { ns: "vitals" }),
      width: "1fr",
      cell: ({ row }) => (
        <LookbackVisibilityCell
          isVisible={!!row.original.excludeFromVitalCountLookback}
          observationIds={row.original.measurements.map(({ observationId }) => observationId)}
        />
      ),
    },
  };

  const {
    views,
    selectedView,
    columns,
    viewId,
    setViewId: setViewIdInternal,
    isLoadingViews,
    isDefaultView,
  } = useTableView<CombinedObservation>({
    page: "T2-Vitals",
    defaultView: defaultViewByUserId[userId] ?? DEFAULT_VITALS_VIEW,
    fieldToColumnConfigMap: fieldToColumnConfigMap,
    viewId: providedViewId,
    setViewId,
    columnFieldsOverride: localOverrides.columnOrderOverride,
  });

  const rawActiveFilters = useMemo<VitalsFilters>(() => {
    const { sorting: _sorting, ...filters } = (selectedView.filter ?? {}) as VitalsFilters & { sorting?: unknown };
    return filters;
  }, [selectedView.filter]);

  const viewConfig = useTableViewConfig<VitalsFilters>({
    isDefaultView,
    userId,
    selectedView,
    columns,
    allColumnConfigMap: fieldToColumnConfigMap,
    defaultView: DEFAULT_VITALS_VIEW,
    setUserDefaultView,
    activeFilters: rawActiveFilters,
    countActiveFilters: countVitalsActiveFilters,
    localOverrides,
  });

  const effectiveFilters = viewConfig.activeFilters;

  // Resolve effective threshold IDs: use explicit selection, or fall back to first program's config IDs
  const effectiveThresholdIds = useMemo<string[] | undefined>(
    () => (effectiveFilters.threshholds?.length ? effectiveFilters.threshholds : vitalPrograms?.firstProgramConfigIds),
    [effectiveFilters.threshholds, vitalPrograms?.firstProgramConfigIds]
  );

  // Default to "valid" when no validStatus filter is explicitly set
  const effectiveValidStatus =
    effectiveFilters.validStatus === "all" ? undefined : effectiveFilters.validStatus ?? "valid";

  const observationsQuery = useVitalsQueryWithFilters({
    filters: {
      reviewed:
        effectiveFilters.reviewed === "all" || effectiveFilters.reviewed === undefined
          ? undefined
          : effectiveFilters.reviewed,
      classifications: effectiveFilters.classification?.triggers.length
        ? { match: effectiveFilters.classification.type, values: effectiveFilters.classification.triggers }
        : undefined,
      sources: effectiveFilters.source,
      enduserIds: effectiveFilters.enduserIds,
      careTeamIds: effectiveFilters.careTeamIds,
      dateRange:
        effectiveFilters.dateRange?.from !== undefined || effectiveFilters.dateRange?.to !== undefined
          ? {
              from:
                effectiveFilters.dateRange?.from !== undefined ? new Date(effectiveFilters.dateRange.from) : undefined,
              to: effectiveFilters.dateRange?.to !== undefined ? new Date(effectiveFilters.dateRange.to) : undefined,
            }
          : undefined,
      validStatus: effectiveValidStatus,
    },
  });
  const [hasSelectedAll, setHasSelectedAll] = useState(false);
  const [selectedObservationIds, setSelectedObservationIds] = useState<string[]>([]);

  const enduserIdsSet = useMemo<Set<string>>(
    () => new Set(observationsQuery.data?.map(({ enduserId }) => enduserId) ?? []),
    [observationsQuery.data]
  );

  const sourceOptions = useMemo<string[]>(
    () =>
      Array.from(
        new Set(
          observationsQuery.data?.reduce<string[]>((acc, { source }) => {
            if (source) {
              acc.push(source);
            }
            return acc;
          }, [])
        )
      ).sort(),
    [observationsQuery.data]
  );

  const endusersQueries = useTellescopeQueries({
    model: "endusers",
    method: "getOne",
    requests: Array.from(enduserIdsSet).map<string>((id) => id),
    combine: (results) =>
      results.reduce<Record<string, TellescopeModelFromName<"endusers">>>((acc, curr) => {
        if (curr.data) {
          acc[curr.data.id] = curr.data;
        }
        return acc;
      }, {}),
  });

  const selectedRows = useMemo(() => {
    if (hasSelectedAll) return observationsQuery.data?.map(({ id }) => id) ?? [];

    return selectedObservationIds;
  }, [hasSelectedAll, observationsQuery.data, selectedObservationIds]);

  const toggleObservationSelection = useCallback(
    (observationId: string) => {
      setSelectedObservationIds((prev) => {
        const isAlreadySelected = hasSelectedAll || prev.includes(observationId);
        if (hasSelectedAll) {
          setSelectedObservationIds(selectedRows.filter((id) => id !== observationId));
        }

        return isAlreadySelected ? prev.filter((id) => id !== observationId) : [observationId, ...prev];
      });
    },
    [hasSelectedAll, selectedRows]
  );

  const onSelectAll = useCallback(() => {
    setHasSelectedAll((prev) => !prev);
  }, []);

  const createTableViewMutation = useTellescopeMutation({
    model: "table_views",
    method: "createOne",
  });

  const deleteTableViewMutation = useTellescopeMutation({
    model: "table_views",
    method: "deleteOne",
  });

  const columnsWithActions = useMemo<VitalsColumnDef[]>(() => {
    if (!canInvalidate) return columns;
    return [
      ...columns,
      {
        id: "Actions",
        label: t("Actions"),
        align: "right",
        sortable: false,
        width: 50,
        cell: ({ row }) => <VitalsActionsCell observation={row.original} />,
      },
    ];
  }, [columns, canInvalidate, t]);

  const virtualizedTableProps = {
    data: observationsQuery.data ?? [],
    getRowId: (row) => row.id,
    columns: columnsWithActions,
    selectedRows,
    onRowSelect: toggleObservationSelection,
    onSelectAll,
    sorting: viewConfig.sorting,
    setSorting: viewConfig.onSortingChange,
    enableDragDrop: false,
    onLoadMore: () => {
      observationsQuery.fetchNextPage();
    },
    hasMoreData: observationsQuery.hasNextPage,
    isLoadingMore: observationsQuery.isLoading,
    isEmpty: observationsQuery.isFetched && !observationsQuery.data?.length,
  } satisfies Partial<VirtualizedDataTableProps<CombinedObservation>>;

  return {
    virtualizedTableProps,
    views,
    selectedView,
    viewId,
    setViewId: setViewIdInternal,
    isLoadingViews,
    createTableViewMutation,
    deleteTableViewMutation,
    TELLESCOPE_2_TABLE_VIEW_PREFIX,
    sourceOptions,
    ...viewConfig,
  };
};
